import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';

import { AdminService } from '../admin.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-pdf-push-to-efile',
    templateUrl: './pdf-push-to-efile.component.html',
    styleUrls: ['./pdf-push-to-efile.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PdfPushToEfileComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();
  private selectedGridRows: any[] = [];
  private modalName = '';

  constructor(private adminService : AdminService,
    private AlertService : GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) { }
  
  @Output('cancel-pdf-push-confirmation') cancelPdfPushConfirmation: EventEmitter<any> = new EventEmitter<any>();
  @Output('request-grid-update') requestGridUpdate: EventEmitter<string> = new EventEmitter<string>();
  
  loading: boolean  = false;
  pushConfirmConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to push pdf\'s to Efile?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
  };
  deleteConfirmConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Delete pdf\'s from Efile?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
  };
  filterParams: any = {};
  globalService: any = {};
  pushData : boolean = false;
  actionNm : string = 'delete-dst-pdf';

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
    this.setConfirmLoading(false);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Legacy source values (pdfPushToEfileController.js):
   * - taxYear/dcnId/submissionId: workspaceFactory.activeScreen.filters.filterParams
   * - modalName: ModalFactory.response.data.modalScreen.modal_name
   * - gridData: selected grid rows transformed into statement payload array
   *
   * In migrated flow, CeHostDialog bridges these into activeScreen.data
   * (`modal_name`, `gridData`) and also exposes `grid-data` / `modal-name`
   * attributes. Prefer current inputs when present, otherwise fall back to
   * activeScreen bridge values.
   */
  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);

    this.selectedGridRows = this.extractSelectedGridRows(activeScreen);

    if (!this.modalName) {
      this.modalName =
        activeScreen?.data?.modal_name
        || activeScreen?.data?.modalName
        || this.globalService?.modalScreen?.modal_name
        || '';
    }

    this.resolveMode();
  }

  private extractSelectedGridRows(activeScreen: any): any[] {
    const dataGridRows = activeScreen?.data?.gridData;
    if (Array.isArray(dataGridRows) && dataGridRows.length > 0) {
      return dataGridRows;
    }

    // Fallbacks used by various grid integrations/workspace snapshots.
    const selectedRows =
      activeScreen?.selectedRows
      || activeScreen?.selected?.rows
      || activeScreen?.data?.selectedRows
      || activeScreen?.data?.selected?.rows;

    if (Array.isArray(selectedRows) && selectedRows.length > 0) {
      return selectedRows;
    }

    return [];
  }

  private resolveMode(): void {
    this.pushData = this.modalName !== this.actionNm;
  }

  private buildGridDataPayload(): string {
    const extractedData = (this.selectedGridRows || []).map((row: any) => {
      const src = row?.data || row || {};
      return {
        statementId: src.STATEMENT_ID ?? src.statementId,
        formId: src.FORM_ID ?? src.formId,
        fileName: src.ATTACHMENT_NAME ?? src.fileName,
        pdfComments: src.COMMENTS ?? src.pdfComments,
        formSub: src.FORM_SUB ?? src.formSub,
      };
    }).filter((x: any) => x.statementId != null && x.statementId !== '');

    return JSON.stringify(extractedData);
  }
  
  pushPdfToEfile():void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.setConfirmLoading(true);
    firstValueFrom(this.adminService.uploadPdfToEfile(
      this.filterParams?.tax_year,
      this.filterParams?.dcn_id,
      this.filterParams?.submission_id,
      this.buildGridDataPayload()
    )).then(
      (response : any) => {
          if (response && response.callSuccess === '1') {
              this.AlertService.openSnackBar('PDF Uploaded to Efile Successfully!!', 'gtw-snackbar--green');
              this.loading = false;
              this.setConfirmLoading(false);
              this.emitRefresh();
          } else {
              throw new Error(response.errorMessage);
          }
      })
      .catch((err : any) => {
          this.emitCancel();
          this.AlertService.openSnackBar(err.message === undefined ?'Something went wrong, please try again!': err.message, 'gtw-snackbar--red');
          this.loading = false;
          this.setConfirmLoading(false);
      }
  );
  
  }

  cancel () : void {
    this.loading = false;
    this.setConfirmLoading(false);
    this.emitCancel();
  };

  deleteForm  ():void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.setConfirmLoading(true);
    firstValueFrom(this.adminService.deleteForm(
      this.filterParams?.tax_year,
      this.filterParams?.dcn_id,
      this.filterParams?.submission_id,
      this.buildGridDataPayload()
    )).then(
        (data) => {
            if (data && data.callSuccess === '1') {
                this.AlertService.openSnackBar('Form Deleted Successfully!', 'gtw-snackbar--green');
                this.loading = false;
                this.setConfirmLoading(false);
                this.emitRefresh();
                
                
            } else {
                throw new Error(data.errorMessage);
            }
        })
        .catch((err : any) => {
            this.emitCancel();
            this.AlertService.openSnackBar(err.message === undefined ?'Something went wrong, please try again!' : err.message, 'gtw-snackbar--red');
            this.loading = false;
            this.setConfirmLoading(false);
        }
    );
  }

  private setConfirmLoading(loading: boolean): void {
    this.pushConfirmConfig = { ...this.pushConfirmConfig, loading };
    this.deleteConfirmConfig = { ...this.deleteConfirmConfig, loading };
  }

  /**
   * Dual-host compatibility:
   * - AngularJS modal wrapper listens to `cancel-pdf-push-confirmation`
   * - Migrated CeHostDialog listens to `request-grid-update`
   */
  private emitCancel(): void {
    this.cancelPdfPushConfirmation.emit();
    this.requestGridUpdate.emit('cancel');
  }

  private emitRefresh(): void {
    this.cancelPdfPushConfirmation.emit();
    this.requestGridUpdate.emit('refresh');
  }


}
